import React from 'react';
import { CheckCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cardTheme } from '../../styles/cardTheme';
import type { CourseEnrollUiKind, CourseEnrollUiResult } from '../../utils/courseEnrollmentLabels';

interface CourseEnrollResultPopupProps {
  result: CourseEnrollUiResult;
  onClose: () => void;
  onPrimary?: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
}

function iconForKind(kind: CourseEnrollUiKind) {
  if (kind === 'success') {
    return (
      <div className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl ${cardTheme.iconBox}`}>
        <CheckCircle className="h-8 w-8" />
      </div>
    );
  }
  if (kind === 'already_enrolled') {
    return (
      <div className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl ${cardTheme.iconBoxGold}`}>
        <Info className="h-8 w-8" />
      </div>
    );
  }
  return (
    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
      <AlertTriangle className="h-8 w-8" />
    </div>
  );
}

const CourseEnrollResultPopup: React.FC<CourseEnrollResultPopupProps> = ({
  result,
  onClose,
  onPrimary,
  secondaryLabel,
  onSecondary,
}) => {
  const handlePrimary = () => {
    if (onPrimary) onPrimary();
    else onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      dir="rtl"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`${cardTheme.shell} relative mx-4 w-full max-w-sm p-6 shadow-xl sm:p-7`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="enroll-result-title"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute left-3 top-3 flex min-h-11 min-w-11 items-center justify-center rounded-xl text-gray-400 hover:bg-green-50 hover:text-green-800"
          aria-label="إغلاق"
        >
          <X size={18} />
        </button>

        <div className="pt-1 text-center">
          {iconForKind(result.kind)}
          <h3
            id="enroll-result-title"
            className={`mb-2 text-xl font-bold leading-snug ${
              result.success ? cardTheme.title : 'text-green-900'
            }`}
          >
            {result.title}
          </h3>
          <p className={`text-sm leading-relaxed sm:text-base ${cardTheme.subtitle}`}>
            {result.message}
          </p>
        </div>

        <div className="mt-6 space-y-2.5">
          <button
            type="button"
            onClick={handlePrimary}
            className={`flex w-full min-h-12 items-center justify-center rounded-xl px-4 text-sm font-bold ${
              result.success ? cardTheme.btnPrimary : cardTheme.btnGold
            }`}
          >
            {result.buttonLabel}
          </button>
          {secondaryLabel && onSecondary && (
            <button
              type="button"
              onClick={onSecondary}
              className={`flex w-full min-h-11 items-center justify-center rounded-xl px-4 text-sm font-bold ${cardTheme.btnOutline}`}
            >
              {secondaryLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseEnrollResultPopup;
